import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import SessionExpiredOverlay from '../components/SessionExpiredOverlay';
import { API_BASE_URL } from '../config/env';
import {
  isPublicAppPath,
  readUnauthorizedMessage,
  resolveRequestUrl,
  shouldReportUnauthorized
} from '../utils/authSession';
import { exitKioskMode } from '../utils/kioskMode';
import { markMundialWelcomePending } from '../config/mundialWelcome';

const AuthContext = createContext(null);
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_PROBE_MS = 45 * 1000;

export function AuthProvider({ children }) {
  const inactivityTimeoutRef = useRef(null);
  const userRef = useRef(null);
  const sessionExpiredRef = useRef(false);
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceActivatedAt, setMaintenanceActivatedAt] = useState(null);
  const [sessionExpired, setSessionExpired] = useState({ open: false, message: '', at: null });

  userRef.current = user;
  sessionExpiredRef.current = sessionExpired.open;

  const clearSessionExpired = useCallback(() => {
    sessionExpiredRef.current = false;
    setSessionExpired({ open: false, message: '', at: null });
  }, []);

  const reportUnauthorized = useCallback((message = 'No autenticado') => {
    if (sessionExpiredRef.current) return;
    if (!userRef.current) return;
    if (isPublicAppPath()) return;
    sessionExpiredRef.current = true;
    setSessionExpired({
      open: true,
      message: String(message || 'No autenticado').trim() || 'No autenticado',
      at: Date.now()
    });
  }, []);

  const reportUnauthorizedRef = useRef(reportUnauthorized);
  reportUnauthorizedRef.current = reportUnauthorized;

  const login = (userData) => {
    clearSessionExpired();
    markMundialWelcomePending();
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshPermissions = useCallback(async () => {
    if (!userRef.current || sessionExpiredRef.current) return;
    setPermissionsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/permissions`, {
        credentials: 'include'
      });
      if (res.status === 401) {
        reportUnauthorizedRef.current(await readUnauthorizedMessage(res));
        return;
      }
      if (!res.ok) return;
      const rows = await res.json();
      const codes = Array.isArray(rows) ? rows.map((r) => String(r.code)) : [];
      setUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, permissions: codes };
        try { localStorage.setItem('user', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
    } catch (e) {
      console.error('Error refrescando permisos:', e);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const logout = async () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }

    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Error al hacer logout en backend:', e);
    } finally {
      await exitKioskMode();
      clearSessionExpired();
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const verifySession = useCallback(async () => {
    if (!userRef.current || sessionExpiredRef.current || isPublicAppPath()) return true;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/permissions`, {
        credentials: 'include'
      });
      if (res.status === 401) {
        reportUnauthorizedRef.current(await readUnauthorizedMessage(res));
        return false;
      }
      return res.ok;
    } catch (_) {
      return true;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      return undefined;
    }

    (async () => {
      try {
        if (user && user.id && !user.permissions && !sessionExpiredRef.current) {
          setPermissionsLoading(true);
          const res = await fetch(`${API_BASE_URL}/api/auth/permissions`, {
            credentials: 'include'
          });
          if (res.status === 401) {
            reportUnauthorizedRef.current(await readUnauthorizedMessage(res));
            return;
          }
          if (res.ok) {
            const rows = await res.json();
            const codes = Array.isArray(rows) ? rows.map((r) => String(r.code)) : [];
            const updated = { ...user, permissions: codes };
            setUser(updated);
            try { localStorage.setItem('user', JSON.stringify(updated)); } catch (_) {}
          } else {
            const updated = { ...user, permissions: [] };
            setUser(updated);
            try { localStorage.setItem('user', JSON.stringify(updated)); } catch (_) {}
          }
        }
      } catch (e) {
        if (user && !user.permissions) {
          const updated = { ...user, permissions: [] };
          setUser(updated);
          try { localStorage.setItem('user', JSON.stringify(updated)); } catch (_) {}
        }
      } finally {
        setPermissionsLoading(false);
      }
    })();

    const resetInactivityTimer = () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      inactivityTimeoutRef.current = setTimeout(() => {
        logout();
      }, INACTIVITY_TIMEOUT_MS);
    };

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, { passive: true });
    });

    resetInactivityTimer();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, [user]);

  useEffect(() => {
    if (!user || isPublicAppPath()) return undefined;

    verifySession();
    const intervalId = setInterval(verifySession, SESSION_PROBE_MS);
    const onFocus = () => { verifySession(); };
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, verifySession]);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const url = resolveRequestUrl(args[0]);
        if (
          userRef.current
          && !sessionExpiredRef.current
          && !isPublicAppPath()
          && shouldReportUnauthorized(url, response.status)
        ) {
          const message = await readUnauthorizedMessage(response);
          reportUnauthorizedRef.current(message);
        }
      } catch (_) { /* ignore interceptor errors */ }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const syncMaintenanceStatus = useCallback(async (isActive) => {
    if (typeof isActive !== 'boolean') return;
    if (!isActive) {
      setMaintenanceActive(true);
      setMaintenanceActivatedAt(Date.now());
    } else {
      setMaintenanceActive(false);
      setMaintenanceActivatedAt(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/frontend-status`, {
          credentials: 'include'
        });
        if (!res.ok) return;
        const json = await res.json();
        const isActive = json && typeof json.isActive !== 'undefined' ? json.isActive : true;
        if (cancelled) return;
        if (isActive === false) {
          setMaintenanceActive((prev) => {
            if (!prev) setMaintenanceActivatedAt(Date.now());
            return true;
          });
        } else {
          setMaintenanceActive(false);
          setMaintenanceActivatedAt(null);
        }
      } catch (_) {
        // ignore transient errors
      }
    };

    checkStatus();
    const id = setInterval(checkStatus, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        refreshPermissions,
        permissionsLoading,
        maintenanceActive,
        maintenanceActivatedAt,
        syncMaintenanceStatus,
        sessionExpired,
        clearSessionExpired,
        reportUnauthorized,
        verifySession
      }}
    >
      {children}
      <SessionExpiredOverlay />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
